import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Business as PropertyIcon,
  Home as RoomIcon,
  CheckCircle as OccupiedIcon,
  Cancel as VacantIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { Loading } from '../Layout';

const PropertyManagement = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [newPropertyName, setNewPropertyName] = useState('');

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/rooms/properties');
      setProperties(response.data);
    } catch (error) {
      console.error('Failed to load properties:', error);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = (property) => {
    setSelectedProperty(property);
    setNewPropertyName(property.property_name);
    setEditDialog(true);
  };

  const handleCloseEdit = () => {
    setEditDialog(false);
    setSelectedProperty(null);
    setNewPropertyName('');
  };

  const handleUpdateProperty = async () => {
    if (!newPropertyName || newPropertyName.trim() === '') {
      toast.error('Please enter a valid property name');
      return;
    }

    if (newPropertyName === selectedProperty.property_name) {
      toast.info('No changes made');
      handleCloseEdit();
      return;
    }

    try {
      const response = await api.put('/api/rooms/properties/bulk-update', {
        old_property_name: selectedProperty.property_name,
        new_property_name: newPropertyName.trim(),
      });

      toast.success(response.data.message);
      handleCloseEdit();
      fetchProperties();
    } catch (error) {
      console.error('Failed to update property:', error);
      toast.error(error.response?.data?.detail || 'Failed to update property name');
    }
  };

  if (loading) {
    return <Loading message="Loading properties..." />;
  }

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <PropertyIcon color="primary" fontSize="large" />
          <Box>
            <Typography variant="h6">Property Management</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage property names and update them in bulk for all associated rooms
            </Typography>
          </Box>
        </Box>

        {properties.length === 0 ? (
          <Alert severity="info">
            No properties found. Create rooms first to see properties here.
          </Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Bulk Update:</strong> Editing a property name here will automatically update
              all rooms associated with that property. This is useful when you want to rename
              a property without editing each room individually.
            </Alert>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Property Name</strong></TableCell>
                    <TableCell align="center"><strong>Total Rooms</strong></TableCell>
                    <TableCell align="center"><strong>Occupied</strong></TableCell>
                    <TableCell align="center"><strong>Vacant</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {properties.map((property, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PropertyIcon color="action" />
                          <Typography variant="body1" fontWeight="medium">
                            {property.property_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={<RoomIcon />}
                          label={property.room_count}
                          size="small"
                          color="default"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={<OccupiedIcon />}
                          label={property.occupied_count}
                          size="small"
                          color="error"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={<VacantIcon />}
                          label={property.vacant_count}
                          size="small"
                          color="success"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEdit(property)}
                          title="Edit Property Name"
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      {/* Edit Property Dialog */}
      <Dialog open={editDialog} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EditIcon color="primary" />
            Edit Property Name
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedProperty && (
            <>
              <Alert severity="warning" sx={{ mb: 3 }}>
                This will update the property name for <strong>{selectedProperty.room_count}</strong> room(s).
              </Alert>
              
              <TextField
                fullWidth
                label="Current Property Name"
                value={selectedProperty.property_name}
                disabled
                margin="normal"
                variant="filled"
              />
              
              <TextField
                fullWidth
                label="New Property Name"
                value={newPropertyName}
                onChange={(e) => setNewPropertyName(e.target.value)}
                margin="normal"
                autoFocus
                required
                helperText="Enter the new property name for all associated rooms"
              />

              <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Affected rooms:</strong> {selectedProperty.room_count} room(s) ({selectedProperty.occupied_count} occupied, {selectedProperty.vacant_count} vacant)
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button
            onClick={handleUpdateProperty}
            variant="contained"
            color="primary"
            disabled={!newPropertyName || newPropertyName.trim() === ''}
          >
            Update Property
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PropertyManagement;
